import { useReducer, useEffect, useCallback, type ReactNode } from 'react';
import { api } from '../api/client';
import { type Customer, type AuthState, AuthContext } from './AuthContext';

type AuthAction =
  | { type: 'LOGIN_START' }
  | { type: 'LOGIN_SUCCESS'; customer: Customer }
  | { type: 'LOGIN_FAILURE'; error: string }
  | { type: 'LOGOUT' }
  | { type: 'SESSION_CHECK_START' }
  | { type: 'SESSION_CHECK_SUCCESS'; customer: Customer }
  | { type: 'SESSION_CHECK_FAILURE' };

const initialState: AuthState = {
  authenticated: false,
  loading: true,
  customer: null,
  error: null,
};

function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'LOGIN_START':
      return { ...state, loading: true, error: null };
    case 'LOGIN_SUCCESS':
      return { authenticated: true, loading: false, customer: action.customer, error: null };
    case 'LOGIN_FAILURE':
      return { authenticated: false, loading: false, customer: null, error: action.error };
    case 'LOGOUT':
      return { authenticated: false, loading: false, customer: null, error: null };
    case 'SESSION_CHECK_START':
      return { ...state, loading: true };
    case 'SESSION_CHECK_SUCCESS':
      return { authenticated: true, loading: false, customer: action.customer, error: null };
    case 'SESSION_CHECK_FAILURE':
      return { authenticated: false, loading: false, customer: null, error: null };
  }
}

/**
 * GET /api/v1/auth/session's body (auth_handler's handle_session). The
 * customer's details are flat fields, not a nested object.
 */
interface SessionResponse {
  authenticated: boolean;
  customer_id?: string;
  customer_name?: string;
  customer_email?: string;
}

function customerFromSession(session: SessionResponse): Customer {
  return {
    id: session.customer_id ?? '',
    name: session.customer_name ?? '',
    email: session.customer_email ?? '',
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(authReducer, initialState);

  const checkSession = useCallback(async () => {
    dispatch({ type: 'SESSION_CHECK_START' });
    try {
      const data = await api.get<SessionResponse>('/api/v1/auth/session');
      if (data?.authenticated) {
        dispatch({ type: 'SESSION_CHECK_SUCCESS', customer: customerFromSession(data) });
      } else {
        dispatch({ type: 'SESSION_CHECK_FAILURE' });
      }
    } catch {
      dispatch({ type: 'SESSION_CHECK_FAILURE' });
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post('/api/v1/auth/logout');
    } catch {
      // Signed out locally either way; the server session expires on its own.
    } finally {
      dispatch({ type: 'LOGOUT' });
    }
  }, []);

  useEffect(() => {
    checkSession();
  }, [checkSession]);

  return (
    <AuthContext.Provider value={{ ...state, logout, checkSession }}>
      {children}
    </AuthContext.Provider>
  );
}
